/* eslint-disable @typescript-eslint/no-use-before-define */
import { EventEmitter } from 'events';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';

import {
  CacheConfig,
  RESPONSE_CODE,
  Filter,
  Format,
  Response,
  RequestOption,
  ErrorResponseHandler,
  REQUEST_SOURCE,
  YoudaoResponse,
  RequestConfig,
  ExportResponse,
} from './define';
import { PandaNamespace } from '../shared/define/PandaNamespace';

declare global {
  interface Window {
    PandaNamespace: PandaNamespace;
  }
}

window.PandaNamespace = window.PandaNamespace || {};

axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.headers.post.Accept = 'application/json';
axios.defaults.withCredentials = true;

function baseFormat<T, U>(response: Response<T>, format?: Format<T, U>) {
  let handler = null;
  switch (response.code) {
    case RESPONSE_CODE.CORRECT:
      if (format) {
        return format(response.result);
      }
      return response.result;
    case RESPONSE_CODE.UN_LOGIN:
      handler = BaseCache.ERROR_HANDLER[RESPONSE_CODE.UN_LOGIN];
      if (handler) {
        handler();
      }
      return null;
    case RESPONSE_CODE.MODAL:
      handler = BaseCache.ERROR_HANDLER[RESPONSE_CODE.MODAL];
      if (handler) {
        handler(response.message);
      }
      return null;
    case RESPONSE_CODE.PERMISSION_DENIED:
      handler = BaseCache.ERROR_HANDLER[RESPONSE_CODE.PERMISSION_DENIED];
      if (handler) {
        handler();
      }
      return null;
    case RESPONSE_CODE.SERVER_ERROR:
      handler = BaseCache.ERROR_HANDLER[RESPONSE_CODE.SERVER_ERROR];
      if (handler) {
        handler();
      }
      return null;
    case RESPONSE_CODE.ACCOUNT_CANCELLATION:
      handler = BaseCache.ERROR_HANDLER[RESPONSE_CODE.MODAL];
      if (handler) {
        handler('帐号已注销，无法登录。<br />如需帮助请访问网址：<a href="https://ytiny.cn/pR4s801" target="_blank">https://ytiny.cn/pR4s801</a>');
      }
      return null;
    case RESPONSE_CODE.NOT_READY:
      handler = BaseCache.ERROR_HANDLER[RESPONSE_CODE.MODAL];
      if (handler) {
        handler('注销需重新验证帐号，请退出登录后，重新登录后再进行注销操作。');
      }
      return null;
    case RESPONSE_CODE.NOTIFY:
    case RESPONSE_CODE.TOAST:
      handler = BaseCache.ERROR_HANDLER[RESPONSE_CODE.NOTIFY];
      if (handler) {
        handler(response.message);
      }
      return null;
    case RESPONSE_CODE.TIMEOUT:
      handler = BaseCache.ERROR_HANDLER[RESPONSE_CODE.TIMEOUT];
      if (handler) {
        handler();
      }
      return null;
    case RESPONSE_CODE.PARAM_ERROR:
      handler = BaseCache.ERROR_HANDLER[RESPONSE_CODE.PARAM_ERROR];
      if (handler) {
        handler();
      }
      return null;
    default:
      return response;
  }
}

function keFormat<T, U>(response: YoudaoResponse<T>, format?: Format<YoudaoResponse<T>, U>) {
  if (format) {
    return format(response);
  }
  return response;
}

// const formatMap = {
//   [REQUEST_SOURCE.PANDA]: baseFormat,
//   [REQUEST_SOURCE.YOUDAO]: keFormat,
// };

function formatWrapper<T, U>(
  format?: Format<T | YoudaoResponse<T>, U>,
  source = REQUEST_SOURCE.PANDA,
) {
  return (value: AxiosResponse<Response<T> | YoudaoResponse<T>>) => {
    let newData;
    if (source === REQUEST_SOURCE.PANDA) {
      newData = baseFormat(value.data as Response<T>, format);
    } else if (source === REQUEST_SOURCE.YOUDAO) {
      newData = keFormat(value.data as YoudaoResponse<T>, format);
    }

    const response = {
      // data: formatMap[source](value.data, format)
      data: newData,
      status: value.status,
      statusText: value.statusText,
      headers: value.headers,
      config: value.config,
    };

    return response;
  };
}

function filterWrapper<T, U, P>(filter?: Filter<T, U, P>) {
  return (value: AxiosRequestConfig) => {
    const request = { ...value };

    if (request.data && filter) {
      request.data = filter({
        data: request.data,
      });
    } else if (request.params && filter) {
      request.params = filter({
        params: request.params,
      });
    }

    return request;
  };
}

function getLeduAuth() {
  try {
    const auth = window.localStorage.getItem('authHeader');
    return auth || null;
  } catch (e) {
    return null;
  }
}

export default abstract class BaseCache extends EventEmitter {
  static ERROR_HANDLER: ErrorResponseHandler = {};

  static LOADING: {
    show?: () => void;
    hide?: () => void;
  } = {};

  static EVENT_POLLING_ING = 'EVENT_POLLING_ING';

  static EVENT_POLLING_DONE = 'EVENT_POLLING_DONE';

  private configs: CacheConfig;

  private times: Map<string, number> = new Map();

  constructor(configs: CacheConfig) {
    super();
    this.configs = configs;
  }

  doSendRequest<T, U>(key: string, option: RequestOption<T, U>) {
    const config = this.configs[key];

    if (config) {
      if (BaseCache.LOADING.show && config.showLoading) {
        BaseCache.LOADING.show();
      }
      const cache = axios.create({
        baseURL: config.baseURL || '/',
      });

      cache.interceptors.request.use(filterWrapper(config.filter));
      cache.interceptors.response.use(formatWrapper(config.format, config.source));

      const axiosOption: RequestConfig = { ...option };

      delete axiosOption.polling;

      const auth = getLeduAuth();
      if (auth) {
        axiosOption.headers = {
          ...(axiosOption.headers || {}),
          AUTH: auth,
        };
      }

      if (config.method === 'get') {
        axiosOption.params = {
          ...axiosOption.params,
          t: +new Date(),
        };
      }

      if (config.requestType === 'formData') {
        axiosOption.headers = {
          ...(axiosOption.headers || {}),
          'Content-Type': 'application/x-www-form-urlencoded',
        };
        axiosOption.data = qs.stringify(axiosOption.data);
      }

      return cache
        .request({ ...axiosOption, url: config.url, method: config.method })
        .then((response) => {
          if (BaseCache.LOADING.hide && config.showLoading) {
            BaseCache.LOADING.hide();
          }
          if (response.data !== null) {
            return response.data;
          }
          return Promise.reject(new Error('请求数据为空'));
        }).catch(() => {
          if (BaseCache.LOADING.hide && config.showLoading) {
            BaseCache.LOADING.hide();
          }
          return Promise.reject(new Error('请求失败'));
        });
    }
    return Promise.reject();
  }

  doRequestWithPolling<T, U>(key: string, option: RequestOption<T, U>) {
    if (!option.polling) {
      return;
    }
    const { timeInterval } = option.polling;

    const clear = () => {
      const timer = this.times.get(key);
      if (timer) {
        window.clearTimeout(timer);
      }
    };

    const next = () => {
      this.doSendRequest(key, option).then((res: ExportResponse) => {
        const { downloadUrl, totalPage, currentPage } = res || {};
        if (downloadUrl) {
          clear();
          BaseCache.download(downloadUrl);
          this.emit(BaseCache.EVENT_POLLING_DONE);
        } else {
          const timer = setTimeout(
            next,
            timeInterval,
          );
          this.times.set(key, timer as unknown as number);
          this.emit(BaseCache.EVENT_POLLING_ING, {
            totalPage,
            currentPage,
          });
        }
      }).catch(() => {
        clear();
      });
    };

    next();
  }

  private static download(url: string) {
    window.location.href = url.indexOf('http') !== -1 ? url.substring(url.indexOf('//')) : url;
  }

  doExport<T, U>(option: RequestOption<T | undefined, U | undefined>,
    exportConfigKey: string, pollingConfigKey: string) {
    this.doSendRequest(exportConfigKey, option)
      .then((res: ExportResponse) => {
        if (res && res.downloadUrl) {
          this.emit(BaseCache.EVENT_POLLING_DONE);
          BaseCache.download(res.downloadUrl);
          return;
        }
        this.doRequestWithPolling(pollingConfigKey, option);
      }).catch(() => {
        this.emit(BaseCache.EVENT_POLLING_DONE);
      });
  }
}

// 格式化参数
function formatParams(data: {[key: string]: string}) {
  const arr = [];
  for (const name in data) {
    arr.push(`${encodeURIComponent(name)}=${encodeURIComponent(data[name])}`);
  }
  arr.push((`t=${Math.random()}`).replace('.', ''));
  return arr.join('&');
}

export const getJSON = (options: {
  url: string;
  callbackName?: string;
  callback?: string;
  data: {[key: string]: string};
  time?: number;
  success?: Function;
  fail?: Function;
}) => {
  options = options || {};
  options.callback = options.callback || 'callback';
  if (!options.url) {
    throw new Error('参数不合法');
  }

  // 创建 script 标签并加入到页面中
  const callbackName = options.callbackName || (`jsonp_${Math.random()}`).replace('.', '');
  const oHead = document.getElementsByTagName('head')[0];
  options.data[options.callback] = callbackName;
  const params = formatParams(options.data);
  const oS = document.createElement('script');
  let oSTimer: number;
  oHead.appendChild(oS);

  // 创建jsonp回调函g数
  // @ts-ignore
  window[callbackName] = function (res: any) {
    oHead.removeChild(oS);
    window.clearTimeout(oSTimer);
    // @ts-ignore
    window[callbackName] = null;
    options.success && options.success(res);
  };

  // 发送请求
  oS.src = `${options.url}?${params}`;

  // 超时处理
  if (options.time) {
    oSTimer = window.setTimeout(() => {
      // @ts-ignore
      window[callbackName] = null;
      oHead.removeChild(oS);
      options.fail && options.fail({ message: '超时' });
    }, options.time);
  }
};
